import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CARD_SECTION_TITLE_CLASS,
  FORM_CONTROL_CLASS,
  FORM_LABEL_CLASS,
  Modal,
  TextButton,
} from '@safetyhub/ui';
import { companyTrustLevel, formatPhoneInput, isValidPhone } from '@safetyhub/shared';
import {
  companiesApi,
  type ClaimConfirmResult,
  type CompanyVerificationStatus,
} from '../../lib/companies';
import { extractApiError } from '../../lib/api';
import { normalizePhoneInput } from '../../lib/phone';
import { formatCountdown } from '../../lib/countdown';
import { useToastStore } from '../../stores/toast';
import { MODAL_LAYER, type ModalLayer } from '../modalLayers';

interface CompanyClaimSectionProps {
  /** 자기 회사 id (= 조직 id). 서버는 이 둘이 같을 때만 클레임을 허용한다. */
  companyId: string;
  /**
   * 국세청 4필드 진위 확인 축(Company.verificationStatus).
   * ⚠️ 조직 화면 상단의 '기관 자격 인증'(agencyVerificationStatus)과는 다른 축이다.
   */
  ntsVerificationStatus?: CompanyVerificationStatus;
  /** 인증(클레임) 확정 시각. null이면 미인증. */
  claimedAt?: string | null;
  /** 모달 대표자명 초기값 — 사업자 정보에 저장된 대표자명. */
  defaultCeoName?: string;
  /** 인증 확정 후 부모가 조직 정보를 다시 읽도록. */
  onClaimed: () => void;
  /** 이 섹션이 여는 모달의 층. 페이지에서 직접 열므로 기본은 root. */
  layer?: ModalLayer;
}

type ClaimStep = 'request' | 'confirm' | 'done';

export default function CompanyClaimSection({
  companyId,
  ntsVerificationStatus,
  claimedAt,
  defaultCeoName,
  onClaimed,
  layer = MODAL_LAYER.root,
}: CompanyClaimSectionProps) {
  const { showToast } = useToastStore();

  // 인증 직후 부모 refetch를 기다리지 않고 배지를 뒤집기 위한 로컬 우선값.
  // refetch가 돌아오면 prop도 같은 값이 되므로 서로 어긋나지 않는다.
  const [confirmedAt, setConfirmedAt] = useState<string | null>(null);
  const effectiveClaimedAt = confirmedAt ?? claimedAt ?? null;

  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<ClaimStep>('request');
  const [ceoName, setCeoName] = useState('');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [mergeResult, setMergeResult] = useState<ClaimConfirmResult | null>(null);

  const codeInputRef = useRef<HTMLInputElement>(null);

  // 저장은 2축(검증 enum + claimedAt)이고 표시 단계만 shared 헬퍼로 합친다.
  // effectiveClaimedAt이 로컬 우선값을 이미 반영하므로 인증 직후에도 곧바로 CANONICAL이 된다.
  const trustLevel = companyTrustLevel({
    verificationStatus: ntsVerificationStatus,
    claimedAt: effectiveClaimedAt,
  });
  const isClaimed = trustLevel === 'CANONICAL';
  /** 클레임 버튼을 열 수 있는 단계 — 4필드 검증까지 끝났고 아직 미클레임 */
  const canClaim = trustLevel === 'INFO_VERIFIED';

  // 만료까지 남은 시간 — 서버가 준 절대 시각 기준이라 탭이 멈춰 있어도 어긋나지 않는다.
  useEffect(() => {
    if (step !== 'confirm' || !expiresAt) {
      setRemainingSeconds(0);
      return;
    }
    const tick = () => {
      const left = Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
      setRemainingSeconds(left);
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [step, expiresAt]);

  // 코드 단계로 넘어가면 입력칸에 바로 커서를 둔다.
  // base Modal의 initialFocusRef는 '열림 순간'에만 적용돼(단계 전환에는 반응하지 않는다) 여기서 직접 옮긴다.
  useEffect(() => {
    if (isOpen && step === 'confirm') codeInputRef.current?.focus();
  }, [isOpen, step]);

  const openModal = () => {
    setStep('request');
    setCeoName(defaultCeoName ?? '');
    setPhone('');
    setCode('');
    setExpiresAt(null);
    setMergeResult(null);
    setIsOpen(true);
  };

  const closeModal = () => {
    if (isSubmitting) return;
    setIsOpen(false);
    // 인증까지 마쳤다면 닫을 때 부모를 갱신한다(성공 단계에서 닫는 경로가 여럿이라 여기 모은다).
    if (step === 'done') onClaimed();
  };

  const handleRequest = async () => {
    if (!ceoName.trim()) {
      showToast('대표자명을 입력해주세요.', 'error');
      return;
    }
    if (!isValidPhone(phone)) {
      showToast('올바른 휴대폰 번호를 입력해주세요.', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await companiesApi.requestClaim(companyId, companyId, {
        ceoName: ceoName.trim(),
        phone,
      });
      setExpiresAt(result.expiresAt);
      setCode('');
      setStep('confirm');
      showToast('인증번호를 발송했습니다.', 'success');
    } catch (err) {
      showToast(extractApiError(err).message, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirm = async () => {
    if (code.length !== 6) {
      showToast('6자리 인증번호를 입력해주세요.', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await companiesApi.confirmClaim(companyId, companyId, {
        ceoName: ceoName.trim(),
        phone,
        code,
      });
      setMergeResult(result);
      setConfirmedAt(result.claimedAt);
      setStep('done');
    } catch (err) {
      showToast(extractApiError(err).message, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDone = () => {
    setIsOpen(false);
    onClaimed();
  };

  const claimedLabel = useMemo(
    () =>
      effectiveClaimedAt
        ? new Date(effectiveClaimedAt).toLocaleDateString('ko-KR')
        : null,
    [effectiveClaimedAt],
  );

  const modalFooter = (() => {
    if (step === 'request') {
      return (
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={closeModal} disabled={isSubmitting}>
            취소
          </Button>
          <Button type="button" variant="primary" onClick={handleRequest} disabled={isSubmitting}>
            {isSubmitting ? '발송 중...' : '인증번호 받기'}
          </Button>
        </div>
      );
    }
    if (step === 'confirm') {
      return (
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={closeModal} disabled={isSubmitting}>
            취소
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleConfirm}
            disabled={isSubmitting || code.length !== 6}
          >
            {isSubmitting ? '확인 중...' : '인증 완료'}
          </Button>
        </div>
      );
    }
    return (
      <div className="flex justify-end">
        <Button type="button" variant="primary" onClick={handleDone}>
          확인
        </Button>
      </div>
    );
  })();

  return (
    <Card padding="xl" radius="lg" elevation="md">
      <div className="mb-1 flex items-center gap-2">
        <h3 className={CARD_SECTION_TITLE_CLASS}>회사 인증</h3>
        {isClaimed && <Badge tone="emerald">인증 완료</Badge>}
      </div>

      {isClaimed ? (
        <p className="text-sm text-ink-500">
          대표자 휴대폰 인증으로 이 회사 정보의 정본으로 등록되었습니다
          {claimedLabel ? ` (${claimedLabel})` : ''}. 같은 사업자번호의 회사 정보는 이 조직에서만
          수정할 수 있습니다.
        </p>
      ) : (
        <>
          <p className="mb-4 text-sm text-ink-500">
            대표자 휴대폰 인증을 마치면 이 회사 정보의 정본으로 등록됩니다. 정본으로 등록되면 회사
            정보의 수정 권한이 이 조직에만 주어집니다.
          </p>
          {canClaim ? (
            <Button type="button" variant="primary" onClick={openModal}>
              대표자 인증으로 정본 등록
            </Button>
          ) : (
            <>
              <div className="mb-4 rounded-card border border-amber-200 bg-amber-50 p-4">
                <p className="text-sm text-amber-800">
                  사업자 정보 4필드 검증(국세청)을 먼저 완료해야 합니다. 위 사업자 정보에서
                  사업자등록번호·회사명·대표자명·개업일을 모두 채우고 인증을 마쳐 주세요.
                </p>
              </div>
              <Button type="button" variant="primary" disabled>
                대표자 인증으로 정본 등록
              </Button>
            </>
          )}
        </>
      )}

      <Modal
        open={isOpen}
        onClose={closeModal}
        title="회사 인증"
        size="md"
        overlayZClassName={layer}
        closeOnOverlay={!isSubmitting}
        closeOnEsc={!isSubmitting}
        footer={modalFooter}
      >
        {step === 'request' && (
          <div className="space-y-4">
            <p className="text-sm text-ink-500">
              국세청에 등록된 대표자명과 대표자 휴대폰 번호를 입력하면 인증번호를 문자로
              보내드립니다.
            </p>
            <div>
              <label htmlFor="claim-ceo-name" className={FORM_LABEL_CLASS}>
                대표자명 <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                id="claim-ceo-name"
                value={ceoName}
                onChange={(e) => setCeoName(e.target.value)}
                disabled={isSubmitting}
                className={FORM_CONTROL_CLASS}
                placeholder="예: 홍길동"
              />
            </div>
            <div>
              <label htmlFor="claim-phone" className={FORM_LABEL_CLASS}>
                대표자 휴대폰 번호 <span className="text-rose-500">*</span>
              </label>
              <input
                type="tel"
                id="claim-phone"
                value={formatPhoneInput(phone)}
                onChange={(e) => setPhone(normalizePhoneInput(e.target.value))}
                disabled={isSubmitting}
                className={FORM_CONTROL_CLASS}
                placeholder="예: 010-1234-5678"
              />
            </div>
          </div>
        )}

        {step === 'confirm' && (
          <div className="space-y-4">
            <p className="text-sm text-ink-500">
              {formatPhoneInput(phone)}로 보낸 인증번호 6자리를 입력해주세요.
            </p>
            <div>
              <label htmlFor="claim-code" className={FORM_LABEL_CLASS}>
                인증번호 <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <input
                  ref={codeInputRef}
                  type="text"
                  id="claim-code"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  disabled={isSubmitting}
                  className={FORM_CONTROL_CLASS}
                  placeholder="6자리 입력"
                />
                {remainingSeconds > 0 && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-rose-500">
                    {formatCountdown(remainingSeconds)}
                  </span>
                )}
              </div>
              {expiresAt && remainingSeconds === 0 && (
                <p className="mt-1 text-xs text-rose-500">
                  인증번호가 만료되었습니다. 다시 발송해 주세요.
                </p>
              )}
            </div>
            <TextButton
              onClick={() => setStep('request')}
              disabled={isSubmitting}
              className="text-sm"
            >
              번호 다시 입력 / 인증번호 재발송
            </TextButton>
          </div>
        )}

        {step === 'done' && (
          <div className="space-y-3">
            <p className="text-sm font-medium text-ink-700">
              회사 인증이 완료되었습니다. 이제 이 조직이 회사 정보의 정본입니다.
            </p>
            {mergeResult && mergeResult.mergedCount > 0 && (
              <p className="text-sm text-ink-500">
                같은 사업자번호의 중복 정보 {mergeResult.mergedCount}건이 정리되었습니다
                {mergeResult.reattachedSiteCount > 0
                  ? ` (현장 ${mergeResult.reattachedSiteCount}곳이 이 회사로 연결되었습니다)`
                  : ''}
                .
              </p>
            )}
            {mergeResult && mergeResult.skippedCompanyIds.length > 0 && (
              <p className="text-sm text-ink-500">
                다른 조직이 입력한 정보가 있는 {mergeResult.skippedCompanyIds.length}건은 그대로
                두었습니다.
              </p>
            )}
          </div>
        )}
      </Modal>
    </Card>
  );
}
